import React, { useState, useRef } from 'react';
import { X, Send, AlertCircle, CheckCircle2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useUser } from '@clerk/clerk-react';
import { CLS_BTN_PRIMARY, CLS_INPUT, TURNSTILE_SITE_KEY } from '../constants';
import { sendPlatformContact } from '../services/webhook';
import { Turnstile, TurnstileInstance } from '@marsidev/react-turnstile';

interface ContactModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export const ContactModal: React.FC<ContactModalProps> = ({ isOpen, onClose }) => {
    const { t } = useTranslation(["common", "home", "search", "listing", "profile", "add_listing", "admin"]);
    const { user } = useUser();
    const [email, setEmail] = useState(user?.primaryEmailAddress?.emailAddress || '');
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);
    const MAX_MESSAGE_LENGTH = 2000;

    // Turnstile State
    const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
    const turnstileRef = useRef<TurnstileInstance>(null);

    // Reset Turnstile when modal closes
    React.useEffect(() => {
        if (!isOpen) {
            setTurnstileToken(null);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const trimmedEmail = email.trim();
        const trimmedMessage = message.trim();

        if (!trimmedEmail || !trimmedMessage) {
            setError(t('contact_form.fields_error'));
            return;
        }

        if (!turnstileToken) {
            setError(t('common:error') || 'Prosím, potvrďte, že nie ste robot.');
            return;
        }

        if (trimmedMessage.length > MAX_MESSAGE_LENGTH) {
            setError(t('contact.send_error'));
            return;
        }

        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
            setError(t('contact.send_error'));
            return;
        }


        setLoading(true);
        setError(null);

        try {
            const result = await sendPlatformContact({
                owner_email: 'info@espacero.com', // General company email for contact form
                customer_email: trimmedEmail,
                customer_name: user?.fullName || 'Anonymous',
                message: trimmedMessage,
                listing_title: 'Všeobecný kontakt',
                listing_id: 'general_contact',
                type: 'contact',
                turnstileToken
            });

            if (!result.success) {
                throw new Error((result as any).error || t('contact.send_error'));
            }

            setSuccess(true);
            setTimeout(() => {
                onClose();
                setSuccess(false);
                setMessage('');
                setTurnstileToken(null);
            }, 3000);
        } catch (err: any) {
            setError(err.message || t('contact.send_error'));
            turnstileRef.current?.reset();
            setTurnstileToken(null);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 animate-fadeIn">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-stone-900/60 backdrop-blur-sm"
                onClick={onClose}
            />

            {/* Modal Content */}
            <div className="relative bg-white dark:bg-stone-900 rounded-3xl w-full max-w-lg shadow-2xl p-6 md:p-8 transform transition-all scale-100 border border-stone-100 dark:border-stone-800 max-h-[calc(100vh-2rem)] overflow-y-auto">
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-stone-400 hover:text-stone-600 dark:hover:text-stone-200 bg-stone-50 dark:bg-stone-800 p-2 rounded-full transition-colors"
                >
                    <X size={20} />
                </button>

                <div className="text-center mb-6">
                    <h2 className="text-2xl font-bold text-primary dark:text-accent-terra mb-2">{t('contact_form.title')}</h2>
                    <p className="text-stone-500 dark:text-stone-400 text-sm">
                        {t('contact_form.subtitle')}
                    </p>
                </div>

                {success ? (
                    <div className="flex flex-col items-center justify-center py-8 text-center animate-fadeIn">
                        <div className="w-16 h-16 bg-accent-terra/10 dark:bg-accent-terra/10 text-primary dark:text-accent-terra rounded-full flex items-center justify-center mb-4">
                            <CheckCircle2 size={32} />
                        </div>
                        <h3 className="text-xl font-bold text-primary dark:text-white mb-2">{t('contact_form.success_title')}</h3>
                        <p className="text-stone-500 dark:text-stone-400">{t('contact_form.success_desc')}</p>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label className="block text-sm font-bold text-stone-800 dark:text-stone-200 mb-2">{t('contact_form.email_label')}</label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className={CLS_INPUT}
                                placeholder="name@example.com"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-bold text-stone-800 dark:text-stone-200 mb-2">{t('contact_form.message_label')}</label>
                            <textarea
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                className={`${CLS_INPUT} min-h-[120px]`}
                                placeholder={t('contact_form.message_placeholder')}
                                maxLength={MAX_MESSAGE_LENGTH}
                                required
                            />
                        </div>

                        {/* Cloudflare Turnstile */}
                        <div className="py-2 flex justify-center">
                            <Turnstile
                                ref={turnstileRef}
                                siteKey={TURNSTILE_SITE_KEY}
                                onSuccess={(token) => setTurnstileToken(token)}
                                onError={() => {
                                    setTurnstileToken(null);
                                    setError('Chyba pri overovaní Turnstile.');
                                }}
                                onExpire={() => setTurnstileToken(null)}
                                options={{ theme: 'auto', size: 'flexible' }}
                            />
                        </div>

                        {error && (
                            <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-3 rounded-xl border border-red-100 dark:border-red-900/30">
                                <AlertCircle size={16} />
                                {error}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={loading}
                            className={`w-full flex items-center justify-center gap-2 ${CLS_BTN_PRIMARY} ${loading ? 'opacity-70 cursor-wait' : ''}`}
                        >
                            {loading ? (
                                t('contact_form.sending')
                            ) : (
                                <>
                                    <Send size={18} /> {t('contact_form.send_button')}
                                </>
                            )}
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
};
